// Guest mode storage using localStorage
// Mirrors the MongoDB API structure for seamless migration

const STORAGE_KEYS = {
  TRANSACTIONS: 'cashforth_guest_transactions',
  SETTINGS: 'cashforth_guest_settings',
  BANNER_DISMISSED: 'cashforth_banner_dismissed',
  TRANSACTION_COUNT: 'cashforth_transaction_count_since_banner',
};

// Generate unique ID
const generateId = () => {
  return 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Parse a "YYYY-MM-DD" string as a LOCAL-time Date (avoids UTC shift).
// new Date("2026-05-01") is parsed as UTC midnight, which becomes
// April 30 in Western (negative-offset) timezones. Always use this helper.
const parseLocalDate = (s) => {
  if (!s) return null;
  if (s instanceof Date) return new Date(s.getFullYear(), s.getMonth(), s.getDate());
  const parts = String(s).split('T')[0].split('-');
  if (parts.length !== 3) return new Date(s);
  const [y, m, d] = parts.map(Number);
  return new Date(y, m - 1, d);
};

// Format a Date as "YYYY-MM-DD" using LOCAL components (avoids UTC shift
// that toISOString() introduces in positive-offset timezones).
const formatLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Convert ISO weekday (Mon=0..Sun=6 — used by backend / DAY_OPTIONS)
// to JS getDay() weekday (Sun=0..Sat=6).
const isoDowToJs = (isoDow) => (isoDow + 1) % 7;

// ============ TRANSACTIONS ============

export const getGuestTransactions = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.TRANSACTIONS);
    return data ? JSON.parse(data) : [];
  } catch (e) {
    console.error('Failed to get guest transactions:', e);
    return [];
  }
};

export const createGuestTransaction = (data) => {
  const transactions = getGuestTransactions();
  const now = new Date().toISOString();
  const newTransaction = {
    ...data,
    id: generateId(),
    user_id: 'guest',
    skipped_dates: [],
    custom_amounts: data.custom_amounts || {},
    paused: false,
    created_at: now,
    updated_at: now,
  };
  transactions.push(newTransaction);
  localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify(transactions));
  
  // Increment transaction count for banner logic
  incrementTransactionCount();
  
  return newTransaction;
};

export const updateGuestTransaction = (id, updates) => {
  const transactions = getGuestTransactions();
  const index = transactions.findIndex(t => t.id === id);
  if (index === -1) return null;
  
  transactions[index] = {
    ...transactions[index],
    ...updates,
    updated_at: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify(transactions));
  return transactions[index];
};

export const deleteGuestTransaction = (id) => {
  const transactions = getGuestTransactions();
  const filtered = transactions.filter(t => t.id !== id);
  localStorage.setItem(STORAGE_KEYS.TRANSACTIONS, JSON.stringify(filtered));
  return { status: 'deleted', id };
};

export const pauseGuestTransaction = (id) => {
  return updateGuestTransaction(id, { paused: true });
};

export const resumeGuestTransaction = (id) => {
  return updateGuestTransaction(id, { paused: false });
};

// ============ SETTINGS ============

export const getGuestSettings = () => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (data) return JSON.parse(data);
    // Return default settings
    const today = new Date().toISOString().split('T')[0];
    return {
      id: 'guest_settings',
      user_id: 'guest',
      base_balance: 0,
      base_date: today,
      min_balance_alert: null,
      max_balance_alert: null,
    };
  } catch (e) {
    console.error('Failed to get guest settings:', e);
    const today = new Date().toISOString().split('T')[0];
    return {
      id: 'guest_settings',
      base_balance: 0,
      base_date: today,
      min_balance_alert: null,
      max_balance_alert: null,
    };
  }
};

export const updateGuestSettings = (updates) => {
  const settings = getGuestSettings();
  const updated = {
    ...settings,
    ...updates,
    updated_at: new Date().toISOString(),
  };
  
  // Handle clearing alerts
  if (updates.clear_min_alert) {
    updated.min_balance_alert = null;
  }
  if (updates.clear_max_alert) {
    updated.max_balance_alert = null;
  }
  
  localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(updated));
  return updated;
};

// ============ LEDGER CALCULATION ============

const getOccurrenceDates = (transaction, startDate, endDate) => {
  if (transaction.paused) return [];
  
  const dates = [];
  const txStart = parseLocalDate(transaction.start_date);
  const txEnd = transaction.end_date ? parseLocalDate(transaction.end_date) : null;
  
  const effectiveStart = new Date(Math.max(startDate.getTime(), txStart.getTime()));
  const effectiveEnd = txEnd ? new Date(Math.min(endDate.getTime(), txEnd.getTime())) : endDate;
  
  if (effectiveStart > effectiveEnd) return [];
  
  const skipped = new Set(transaction.skipped_dates || []);
  const recurrence = transaction.recurrence_type;
  
  if (recurrence === 'one_time') {
    if (effectiveStart <= txStart && txStart <= effectiveEnd) {
      dates.push(new Date(txStart));
    }
  } else if (recurrence === 'weekly') {
    // transaction.day_of_week is stored in ISO convention (Mon=0..Sun=6)
    // matching the backend / DAY_OPTIONS. Convert to JS getDay() (Sun=0..Sat=6).
    const dow = transaction.day_of_week !== undefined && transaction.day_of_week !== null
      ? isoDowToJs(transaction.day_of_week)
      : txStart.getDay();
    let current = new Date(effectiveStart);
    const daysUntil = (dow - current.getDay() + 7) % 7;
    current.setDate(current.getDate() + daysUntil);
    while (current <= effectiveEnd) {
      if (current >= txStart) dates.push(new Date(current));
      current.setDate(current.getDate() + 7);
    }
  } else if (recurrence === 'bi_weekly') {
    const dow = transaction.day_of_week !== undefined && transaction.day_of_week !== null
      ? isoDowToJs(transaction.day_of_week)
      : txStart.getDay();
    let current = new Date(txStart);
    const daysUntil = (dow - current.getDay() + 7) % 7;
    current.setDate(current.getDate() + daysUntil);
    while (current <= effectiveEnd) {
      if (current >= effectiveStart) dates.push(new Date(current));
      current.setDate(current.getDate() + 14);
    }
  } else if (recurrence === 'semi_monthly') {
    const day1 = transaction.day_of_month || 1;
    const day2 = transaction.second_day_of_month || 15;
    let currentMonth = new Date(effectiveStart.getFullYear(), effectiveStart.getMonth(), 1);
    while (currentMonth <= effectiveEnd) {
      [day1, day2].forEach(day => {
        const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
        const actualDay = Math.min(day, lastDay);
        const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), actualDay);
        if (effectiveStart <= date && date <= effectiveEnd && date >= txStart) {
          dates.push(date);
        }
      });
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }
  } else if (recurrence === 'monthly') {
    const dom = transaction.day_of_month || txStart.getDate();
    let currentMonth = new Date(effectiveStart.getFullYear(), effectiveStart.getMonth(), 1);
    while (currentMonth <= effectiveEnd) {
      const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
      const actualDay = Math.min(dom, lastDay);
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), actualDay);
      if (effectiveStart <= date && date <= effectiveEnd && date >= txStart) {
        dates.push(date);
      }
      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }
  } else if (recurrence === 'quarterly') {
    const dom = transaction.day_of_month || txStart.getDate();
    let currentMonth = new Date(txStart.getFullYear(), txStart.getMonth(), 1);
    while (currentMonth <= effectiveEnd) {
      const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
      const actualDay = Math.min(dom, lastDay);
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), actualDay);
      if (effectiveStart <= date && date <= effectiveEnd && date >= txStart) {
        dates.push(date);
      }
      currentMonth.setMonth(currentMonth.getMonth() + 3);
    }
  } else if (recurrence === 'annual') {
    // Iterate from the transaction's own start year so the very first
    // anniversary isn't skipped when txStart > effectiveStart's year.
    let currentYear = txStart.getFullYear();
    while (currentYear <= effectiveEnd.getFullYear()) {
      const date = new Date(currentYear, txStart.getMonth(), txStart.getDate());
      if (effectiveStart <= date && date <= effectiveEnd && date >= txStart) {
        dates.push(date);
      }
      currentYear++;
    }
  } else if (recurrence === 'custom') {
    const interval = transaction.custom_interval_days || 1;
    let current = new Date(txStart);
    while (current <= effectiveEnd) {
      if (current >= effectiveStart) dates.push(new Date(current));
      current.setDate(current.getDate() + interval);
    }
  } else if (recurrence === 'none') {
    // Sporadic transactions - dates come from custom_amounts keys
    const customAmounts = transaction.custom_amounts || {};
    Object.keys(customAmounts).forEach(dateStr => {
      const date = parseLocalDate(dateStr);
      if (date && effectiveStart <= date && date <= effectiveEnd) {
        dates.push(date);
      }
    });
  }
  
  // Filter skipped dates (use local date string to avoid UTC shift)
  return dates.filter(d => !skipped.has(formatLocalDate(d)));
};

export const getGuestLedger = (days = 30) => {
  const settings = getGuestSettings();
  const transactions = getGuestTransactions();
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const asOf = new Date(today);
  const endDate = new Date(today);
  endDate.setDate(endDate.getDate() + days);
  
  const baseBalance = settings.base_balance || 0;
  const baseDate = settings.base_date ? parseLocalDate(settings.base_date) : today;
  baseDate.setHours(0, 0, 0, 0);
  
  // Calculate starting balance from base_date to as_of
  let startingBalance = baseBalance;
  if (baseDate < asOf) {
    const dayBefore = new Date(asOf);
    dayBefore.setDate(dayBefore.getDate() - 1);
    transactions.forEach(tx => {
      const pastDates = getOccurrenceDates(tx, baseDate, dayBefore);
      const customAmounts = tx.custom_amounts || {};
      const defaultAmount = tx.amount || 0;  // Handle null for sporadic
      pastDates.forEach(d => {
        const dateStr = formatLocalDate(d);
        const amount = customAmounts[dateStr] !== undefined ? customAmounts[dateStr] : defaultAmount;
        startingBalance += amount;
      });
    });
  }
  
  // Generate future entries
  const allEntries = [];
  transactions.forEach(tx => {
    const dates = getOccurrenceDates(tx, asOf, endDate);
    const customAmounts = tx.custom_amounts || {};
    const defaultAmount = tx.amount || 0;  // Handle null for sporadic
    dates.forEach(date => {
      const dateStr = formatLocalDate(date);
      const amount = customAmounts[dateStr] !== undefined ? customAmounts[dateStr] : defaultAmount;
      allEntries.push({
        date,
        dateStr,
        transaction_id: tx.id,
        name: tx.name,
        amount,
        is_hypothetical: tx.is_hypothetical || false,
      });
    });
  });
  
  // Sort by date, then expenses (negative) before income (positive)
  allEntries.sort((a, b) => a.date - b.date || (a.amount < 0 ? 0 : 1) - (b.amount < 0 ? 0 : 1));
  
  // Calculate running balance
  let balance = startingBalance;
  const minAlert = settings.min_balance_alert;
  const maxAlert = settings.max_balance_alert;
  
  const ledgerEntries = allEntries.map(entry => {
    balance += entry.amount;
    return {
      date: entry.dateStr,
      transaction_id: entry.transaction_id,
      name: entry.name,
      amount: entry.amount,
      balance: Math.round(balance * 100) / 100,
      is_hypothetical: entry.is_hypothetical,
      below_min_alert: minAlert !== null && balance < minAlert,
      above_max_alert: maxAlert !== null && balance > maxAlert,
    };
  });
  
  return {
    entries: ledgerEntries,
    starting_balance: Math.round(startingBalance * 100) / 100,
    as_of_date: formatLocalDate(asOf),
    min_balance_alert: minAlert,
    max_balance_alert: maxAlert,
    base_balance: baseBalance,
    base_date: formatLocalDate(baseDate),
  };
};

// ============ BANNER LOGIC ============

export const isBannerDismissed = () => {
  return localStorage.getItem(STORAGE_KEYS.BANNER_DISMISSED) === 'true';
};

export const dismissBanner = () => {
  localStorage.setItem(STORAGE_KEYS.BANNER_DISMISSED, 'true');
  localStorage.setItem(STORAGE_KEYS.TRANSACTION_COUNT, '0');
};

const incrementTransactionCount = () => {
  const count = parseInt(localStorage.getItem(STORAGE_KEYS.TRANSACTION_COUNT) || '0', 10);
  const newCount = count + 1;
  localStorage.setItem(STORAGE_KEYS.TRANSACTION_COUNT, String(newCount));
  
  // Reset banner dismissal after 5 transactions
  if (newCount >= 5 && isBannerDismissed()) {
    localStorage.setItem(STORAGE_KEYS.BANNER_DISMISSED, 'false');
    localStorage.setItem(STORAGE_KEYS.TRANSACTION_COUNT, '0');
  }
};

export const getTransactionCountSinceBanner = () => {
  return parseInt(localStorage.getItem(STORAGE_KEYS.TRANSACTION_COUNT) || '0', 10);
};

// ============ MIGRATION ============

export const hasGuestData = () => {
  const transactions = getGuestTransactions();
  return transactions.length > 0;
};

export const getGuestDataForMigration = () => {
  return {
    transactions: getGuestTransactions(),
    settings: getGuestSettings(),
  };
};

export const clearGuestData = () => {
  localStorage.removeItem(STORAGE_KEYS.TRANSACTIONS);
  localStorage.removeItem(STORAGE_KEYS.SETTINGS);
  localStorage.removeItem(STORAGE_KEYS.BANNER_DISMISSED);
  localStorage.removeItem(STORAGE_KEYS.TRANSACTION_COUNT);
};
